import React from 'react';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Box } from '@lifeomic/chroma-react/components/Box';
import SurveyResponseResults, {
  QuestionResponseResult,
} from '../../../Surveys/util/SurveyResponseResults';
import { Text } from '@lifeomic/chroma-react/components/Text';
import ScoringContainer from './ScoringContainer';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { lighten } from '@mui/material/styles';
import FavoriteSurveyItem from '../../common/Favorite/favorite-survey-item';
import MarkdownViewer from '../../../MarkdownViewer';

export const useStyles = makeStyles<{ hasScore: boolean }>((theme: Theme) => ({
  root: {
    alignItems: 'center',
    display: 'grid',
    gridTemplateColumns: ({ hasScore }) =>
      `2fr 2fr ${hasScore ? '1fr' : ''} ${theme.pxToRem(50)}`,
    padding: theme.spacing(1.5, 4, 1.5, 0),
    width: '100%',
    [theme.breakpoints.up('xl')]: {
      maxWidth: theme.pxToRem(1200),
    },
    '&:hover': {
      background:
        theme.palette.mode === 'dark'
          ? theme.palette.action.hover
          : lighten(theme.palette.black[50], 0.75),
    },
    '&:has(p:empty)': {
      display: 'none',
    },
  },
  question: {
    color: theme.palette.text.secondary,
    maxWidth: '70ch',
    marginLeft: theme.spacing(4),
  },
  divider: {
    margin: 0,
  },
  markdown: {
    fontSize: theme.pxToRem(14),
    lineHeight: theme.pxToRem(22),
    padding: 0,
    color: theme.palette.text.primary,
    '& .public-DraftEditor-content, & .DraftEditor-root': {
      color: theme.palette.text.primary,
    },
    '& span[data-text="true"]': {
      color: theme.palette.text.primary,
    },
  },
  score: {
    color: theme.palette.text.primary,
  },
}));

export type QuestionLineItemOwnProps = {
  item: QuestionResponseResult;
  parentHasScoring: boolean;
  selectedResponse?: fhir.QuestionnaireResponse;
  onFavoriteUpdate?: (resource: fhir.QuestionnaireResponse) => void;
};

export type QuestionLineItemProps = QuestionLineItemOwnProps;

const QuestionLineItem: React.FC<QuestionLineItemProps> = ({
  item,
  parentHasScoring,
  selectedResponse,
  onFavoriteUpdate,
}) => {
  const classes = useStyles({ hasScore: parentHasScoring });

  return (
    <>
      <Divider className={classes.divider} />
      <Box className={classes.root} gap={2}>
        <Text className={classes.question} size="subbody" weight="bold">
          {item.label}
        </Text>
        {SurveyResponseResults.isValueQuestionResult(item) && (
          <>
            <MarkdownViewer
              markdown={item.value}
              viewerClassName={classes.markdown}
            />
            {parentHasScoring && (
              <ScoringContainer>
                <Text className={classes.score} size="subbody">
                  {item.score}
                </Text>
              </ScoringContainer>
            )}
            <FavoriteSurveyItem
              response={selectedResponse}
              item={item}
              onFavoriteUpdate={onFavoriteUpdate}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default React.memo(QuestionLineItem);
